// @ts-check
// SESSION store — Zustand (Overmind retirement, Phase C2 part 1). Per-user,
// client-mutated checkout/session state that is deliberately NOT in the SSR HTML
// (keeps the region HTML identical per visitor + shared-cacheable, #188).
//
// SSR-SAFETY — every field starts at its EMPTY default (null / 0). The two
// PERSISTED, user-specific values (affiliateCode, discountCode) are restored from
// localStorage ONLY post-mount (<SessionStoreHydrator/> in SiteChrome calls
// hydrateFromLocalStorage() in a mount effect — and RegionPageClient's
// QueryParamEffects also invokes it before the entry-time query actions). Because
// nothing reads localStorage during render, the server render and the client's
// FIRST render both see the empty default → no #418/#423 and no per-user leak into
// the micro-cache. This is stricter than the former Overmind path, which restored
// these during a render-time useMemo. (The payment-session fields —
// paymentStartedInfo / shippingAddress / postalCodeForInvoicePreview — are
// ephemeral per checkout and were never persisted; they stay in-memory only.)
//
// PERSISTENCE PARITY / ROLLBACK-SAFETY — affiliateCode + discountCode are written
// to the SAME localStorage keys, in the SAME JSON.stringify shape, under the SAME
// conditions as the former Overmind localStorage effect, so an in-flight
// affiliate/discount survives with NO migration and a rollback to Overmind still
// reads them. In particular (grep operators.js): affiliateCode persists (with a
// companion timestamp + 30-day expiry) ONLY when it arrives via the entry query;
// discountCode persists ONLY via the entry query (updateDiscountCode) — the
// invoice-preview setDiscountCode is in-memory only — and resetDiscountCode clears
// the key.
import { create } from 'zustand'
import { getAutoDiscountCode } from '../lib/expoPromo'

// localStorage keys — UNCHANGED from the Overmind effect (rollback-safe).
export const AFFILIATE_CODE_KEY = 'affiliateCode'
export const AFFILIATE_CODE_DATE_KEY = 'affiliateCodeDate'
export const DISCOUNT_CODE_KEY = 'discountCode'
const AFFILIATE_CODE_TTL_DAYS = 30

// Mirror overmind/effects.localStorage EXACTLY: window-guarded JSON round-trip, so
// the on-disk bytes are identical to what Overmind wrote/read.
/** @param {string} key @returns {any} */
function lsGet(key) {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(key)
  if (raw == null) return null
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}
/** @param {string} key @param {any} value */
function lsSet(key, value) {
  if (typeof window !== 'undefined') window.localStorage.setItem(key, JSON.stringify(value))
}
/** @param {string} key */
function lsRemove(key) {
  if (typeof window !== 'undefined') window.localStorage.removeItem(key)
}

export const useSessionStore = create((set, get) => ({
  // ---- persisted (localStorage), user-specific — SSR-empty until post-mount ----
  /** @type {string|null} */
  affiliateCode: null,
  /** number 0-100; NOT persisted (re-derived from affiliateCode via the API) */
  affiliateDiscountPercentage: 0,
  /** @type {string|null} */
  discountCode: null,

  // ---- ephemeral payment session (never persisted) ----
  /** @type {any} */
  paymentStartedInfo: null,
  /** @type {any} */
  shippingAddress: null,
  /** @type {string|null} */
  postalCodeForInvoicePreview: null,

  /** true once hydrateFromLocalStorage has run (post-mount) */
  _hasHydrated: false,

  /**
   * Restore the persisted affiliate/discount codes from localStorage. Idempotent
   * (guarded by _hasHydrated). Mirrors operators.setAffiliateCodeFromLocalStorage
   * (30-day expiry, purges expired) + setDiscountCodeFromLocalStorage (no expiry).
   * Runs ONLY post-mount, so it never affects the SSR HTML / first client render.
   */
  hydrateFromLocalStorage: () => {
    if (get()._hasHydrated) {
      return
    }
    // affiliateCode — restore only if within the 30-day window; else purge both keys.
    const affiliateCode = lsGet(AFFILIATE_CODE_KEY)
    if (affiliateCode) {
      const affiliateCodeDate = new Date(Number(lsGet(AFFILIATE_CODE_DATE_KEY)))
      const expiryDate = new Date(affiliateCodeDate.getTime())
      expiryDate.setDate(expiryDate.getDate() + AFFILIATE_CODE_TTL_DAYS)
      if (new Date() < expiryDate) {
        set({ affiliateCode })
      } else {
        lsRemove(AFFILIATE_CODE_KEY)
        lsRemove(AFFILIATE_CODE_DATE_KEY)
      }
    }
    // discountCode — restore verbatim (no expiry).
    const discountCode = lsGet(DISCOUNT_CODE_KEY)
    if (discountCode) {
      set({ discountCode })
    }
    // Site-wide promo (lib/expoPromo): every visitor gets the code while it runs.
    // In memory only, so it is never re-sent after the promo ends.
    const autoCode = getAutoDiscountCode()
    if (!discountCode && autoCode) {
      set({ discountCode: autoCode })
    }
    set({ _hasHydrated: true })
  },

  /**
   * Apply the entry-time ?aff-code query param, then (re)derive the discount %.
   * Mirrors actions.updateAffiliateCodeFromQuery = pipe(updateAffiliateCode,
   * updateAffiliateDiscountPercentage):
   *   - a present code is stored + persisted (code + timestamp);
   *   - the % is fetched for the CURRENT code (from the query OR a restored code),
   *     or reset to 0 when there is none.
   * @param {Record<string, string>} query
   */
  applyAffiliateFromQuery: async (query) => {
    const affiliateCode = (query && query['aff-code']) || null
    if (affiliateCode) {
      set({ affiliateCode })
      lsSet(AFFILIATE_CODE_KEY, affiliateCode)
      lsSet(AFFILIATE_CODE_DATE_KEY, Date.now() + '')
    }
    // updateAffiliateDiscountPercentage: read the now-current code (query or restored).
    const code = get().affiliateCode
    if (!code) {
      set({ affiliateDiscountPercentage: 0 })
      return
    }
    try {
      const res = await fetch(`/api/getAffiliateDiscount?affiliateCode=${code}`)
      if (!res.ok) {
        throw new Error(`getAffiliateDiscount ${res.status}`)
      }
      const data = await res.json()
      set({ affiliateDiscountPercentage: data.discountPercentage })
    } catch (e) {
      set({ affiliateDiscountPercentage: 0 })
    }
  },

  /**
   * Apply the entry-time ?discount-code query param. Mirrors
   * actions.updateDiscountCodeFromQuery = pipe(updateDiscountCode): a present code
   * is stored + persisted; an absent one is a no-op (does NOT clear an existing code).
   * @param {Record<string, string>} query
   */
  applyDiscountFromQuery: (query) => {
    const discountCode = (query && query['discount-code']) || null
    if (discountCode) {
      set({ discountCode })
      lsSet(DISCOUNT_CODE_KEY, discountCode)
    }
  },

  /**
   * Set the in-memory discount code WITHOUT persisting. Mirrors
   * actions.setDiscountCode — used by the invoice preview to keep an ELIGIBLE code
   * for the current checkout only (it must not be silently re-sent on the next visit).
   * @param {string|null} discountCode
   */
  setDiscountCode: (discountCode) => set({ discountCode }),

  /** Clear the discount code + its persisted value. Mirrors actions.resetDiscountCode. */
  resetDiscountCode: () => {
    set({ discountCode: null })
    lsRemove(DISCOUNT_CODE_KEY)
  },

  /** @param {any} shippingAddress Mirrors actions.setShippingAddress. */
  setShippingAddress: (shippingAddress) => set({ shippingAddress }),

  /** @param {any} paymentStartedInfo Mirrors actions.setPaymentStartedInfo. */
  setPaymentStartedInfo: (paymentStartedInfo) => set({ paymentStartedInfo }),

  /** @param {string|null} postalCode Mirrors actions.setPostalCodeForInvoicePreview. */
  setPostalCodeForInvoicePreview: (postalCode) =>
    set({ postalCodeForInvoicePreview: postalCode }),
}))

// ---- selectors ----
/** @param {{ affiliateCode: string|null }} s */
export const selectAffiliateCode = (s) => s.affiliateCode
/** @param {{ discountCode: string|null }} s */
export const selectDiscountCode = (s) => s.discountCode
/** @param {{ paymentStartedInfo: any }} s */
export const selectPaymentStartedInfo = (s) => s.paymentStartedInfo
